import { Notification as customNotification } from '../../components/ui/custom-notification';
import get from 'lodash/get';
import {
  all,
  call,
  put,
  takeLatest,
  select,
} from 'redux-saga/effects';
import axios from 'axios';
import axiosConfig from './axiosConfig';
import {
  FETCH_TEAM_MEMBERS,
  DELETE_TEAM_MEMBER,
  ADD_TEAM_MEMBER,
  UPDATE_TEAM_MEMBER,
  FETCH_PROJECT_ROLES,
  FETCH_CERTIFICATIONS,
  FETCH_USERS,
  GET_PRESIGNED_URL,
  UPLOAD_CERTIFICATE,
  CREATE_UPDATE_TEAM_MEMBER,
  DELETE_TEAM_MEMBER_CERT,
  CREATE_UPDATE_PROJECT_LEVEL_ROLES,
  DELETE_PROJECT_LEVEL_ROLE
} from './types';
import {
  fetchTeamMembers as fetchTeamMembersAction,
  fetchTeamMembersSuccess,
  fetchTeamMembersFailure,
  deleteTeamMemberSuccess,
  deleteTeamMemberFailure,
  addTeamMemberSuccess,
  addTeamMemberFailure,
  updateTeamMemberSuccess,
  updateTeamMemberFailure,
  fetchProjectRolesSuccess,
  fetchProjectRolesFailure,
  fetchCertificationsSuccess,
  fetchCertificationsFailure,
  fetchUsersSuccess,
  fetchUsersFailure,
  getPresignedUrlSuccess,
  getPresignedUrlFailure,
  uploadCertificateSuccess,
  uploadCertificateFailure,
  createUpdateTeamMemberSuccess,
  createUpdateTeamMemberFailure,
  deleteTeamMemberCertSuccess,
  deleteTeamMemberCertFailure,
  createUpdateProjectLevelRolesSuccess,
  createUpdateProjectLevelRolesFailure,
  deleteProjectLevelRoleSuccess,
  deleteProjectLevelRoleFailure
} from './actions';

// Helper function to transform API data to frontend format
const transformApiTeamMemberToFrontend = (apiMember) => {
  return {
    id: apiMember.team_member_id.toString(),
    name: `${apiMember.user.first_name} ${apiMember.user.last_name}`,
    role: apiMember.team_member_role,
    email: apiMember.user.email,
    phone: apiMember.user.phone_number || '',
    certifications: apiMember.certificates.map(cert => cert.file_name),
    qualifications: apiMember.certificates.map(cert => cert.file_name),
    documents: apiMember.certificates.map(cert => ({
      id: cert.cert_id.toString(),
      fileName: cert.file_name,
      type: 'certification',
      uploadDate: cert.created_at
    })),
    status: apiMember.status.toLowerCase() === 'active' ? 'active' : 'inactive',
    team_member_id: apiMember.team_member_id,
    user_id: apiMember.user_id,
    project_id: apiMember.project_id,
    project_level_role_id: apiMember.project_level_role_id,
    apiData: apiMember
  };
};

function* fetchTeamMembers({ payload }) {
  try {
    const { projectId } = payload || {};
    let url = '/teamMembers/getAllTeamMembers';

    // Add project_id as query parameter if provided
    if (projectId) {
      url += `?project_id=${projectId}`;
    }

    const response = yield call(axiosConfig.get, url);

    if (response.status === 200) {
      const responseData = get(response, ['data', 'data'], []);

      if (responseData && responseData.platformTeamMembers) {
        const transformedMembers = responseData.platformTeamMembers.map(transformApiTeamMemberToFrontend);
        yield put(fetchTeamMembersSuccess({ data: transformedMembers }));
      } else {
        yield put(fetchTeamMembersSuccess({ data: [] }));
      }
    } else {
      yield put(fetchTeamMembersFailure('Failed to fetch team members'));
    }
  } catch (error) {

    let errorMessage = 'Failed to fetch team members';

    if (error.response) {
      // Server responded with error status
      if (error.response.status === 401) {
        errorMessage = 'Authentication failed. Please log in again.';
      } else if (error.response.status === 403) {
        errorMessage = 'Access denied. You may not have permission to view team members.';
      } else if (error.response.status === 404) {
        errorMessage = 'Team members endpoint not found.';
      } else if (error.response.status === 500) {
        errorMessage = 'Server error occurred while fetching team members.';
      } else if (error.response.data && error.response.data.message) {
        errorMessage = error.response.data.message;
      }
    } else if (error.request) {
      // Request was made but no response received
      errorMessage = 'No response received from server. Please check your network connection.';
    } else if (error.message) {
      // Something else happened
      errorMessage = error.message;
    }

    yield put(fetchTeamMembersFailure(errorMessage));
  }
}

function* deleteTeamMember({ payload }) {
  try {
    const { teamMemberId, projectId } = payload || {};

    if (!teamMemberId) {
      yield put(deleteTeamMemberFailure('Team member ID is required'));
      return;
    }

    const response = yield call(axiosConfig.delete, `/teamMembers/deleteTeamMember/${teamMemberId}`);

    if (response.status === 200) {
      customNotification.success('Success', 'Team member deleted successfully.');
      yield put(deleteTeamMemberSuccess());

      // Refresh team members list after successful deletion to remove deleted member from UI
      const refreshPayload = projectId ? { projectId } : {};
      yield call(fetchTeamMembers, { payload: refreshPayload });
    } else {
      yield put(deleteTeamMemberFailure('Failed to delete team member'));
    }
  } catch (error) {
    customNotification.error('Error', 'Failed to delete team member. Please try again.');
    yield put(deleteTeamMemberFailure(error.message || 'Failed to delete team member'));
  }
}

function* addTeamMember({ payload }) {
  try {

    const apiPayload = payload?.data || payload;
    if (apiPayload.role === 'Welder') {
      const qrPayload = {
        text: apiPayload.user_name,
        title: apiPayload.user_id,
        qr_code_type: 'Welder ID',
        bottom_text: apiPayload.role,
        qr_size: 500,
        error_correction: 'H'
      };
      console.log(qrPayload);
      const apiUrl ="https://uawu2oh2i6.execute-api.us-west-2.amazonaws.com/dev/generate" 

      const response = yield fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': 'data360-GWjjPcCPYmyY0pzu'  // Add API key header
        },
        body: JSON.stringify(qrPayload)
      });
      console.log( "response", response);

      const qrData = yield response.json();
      console.log( "qrData", qrData);

      if (response.ok && qrData.success) {
        const qrImage = qrData.image;

        apiPayload.permissions = {
          ...apiPayload.permissions,
          qr_code: qrImage
        };
      }
    }



    console.log( "apiPayload", apiPayload);

    
    const response = yield call(axiosConfig.post, '/teamMembers/addTeamMember', apiPayload);

    if (response.status === 200) {
      const responseData = get(response, ['data', 'data'], {});
      customNotification.success('Success', 'Team member added successfully.');
      yield put(addTeamMemberSuccess({ data: responseData }));

      // Refresh team members list after successful addition
      // Pass the project_id if available
      const refreshPayload = apiPayload.project_id || apiPayload.projectId ?
        { projectId: apiPayload.project_id || apiPayload.projectId } : {};
      yield call(fetchTeamMembers, { payload: refreshPayload });
    } else {
      yield put(addTeamMemberFailure('Failed to add team member'));
    }
  } catch (error) {
    customNotification.error('Error', 'Failed to add team member. Please try again.');
    yield put(addTeamMemberFailure(error.message || 'Failed to add team member'));
  }
}

function* updateTeamMember({ payload }) {
  try {
    const { teamMemberId, ...updateData } = payload?.data || payload;

    if (!teamMemberId) {
      yield put(updateTeamMemberFailure('Team member ID is required'));
      return;
    }

    const response = yield call(axiosConfig.put, `/teamMembers/updateTeamMember/${teamMemberId}`, updateData);

    if (response.status === 200) {
      const responseData = get(response, ['data', 'data'], {});
      // Success notification is handled by createUpdateTeamMember saga
      yield put(updateTeamMemberSuccess({ data: responseData }));

      // Refresh team members list after successful update
      // Pass the project_id if available
      const refreshPayload = updateData.project_id || updateData.projectId ?
        { projectId: updateData.project_id || updateData.projectId } : {};
      yield call(fetchTeamMembers, { payload: refreshPayload });
    } else {
      yield put(updateTeamMemberFailure('Failed to update team member'));
    }
  } catch (error) {
    customNotification.error('Error', 'Failed to update team member. Please try again.');
    yield put(updateTeamMemberFailure(error.message || 'Failed to update team member'));
  }
}

function* fetchCertifications() {
  try {

    const url = '/tags/getAllTags?tags_type=Certificate';


    const response = yield call(axiosConfig.get, url);

    // Check if response is valid
    if (!response) {
      throw new Error('No response received from API');
    }

    if (response.status === 200) {
      // Try different possible data structures
      let responseData = get(response, ['data', 'data', 'tags'], null);
      if (!responseData) {
        responseData = get(response, ['data', 'tags'], null);
      }
      if (!responseData) {
        responseData = get(response, ['data'], null);
      }


      if (responseData && Array.isArray(responseData)) {
        // Transform the data to match the expected format
        const transformedData = responseData.map((tag) => ({
          label: tag.tags_name || tag.name || tag.label || tag.value || 'Unknown',
          value: tag.tags_name || tag.name || tag.label || tag.value || 'Unknown'
        }));

        yield put(fetchCertificationsSuccess({ data: transformedData }));
      } else {
        yield put(fetchCertificationsSuccess({ data: [] }));
      }
    } else {
      yield put(fetchCertificationsFailure('Failed to fetch certifications'));
    }
  } catch (error) {

    let errorMessage = 'Failed to fetch certifications';

    if (error.response) {
      // Server responded with error status


      if (error.response.status === 404) {
        errorMessage = 'Certifications endpoint not found. Please check the API configuration.';
      } else if (error.response.status === 401) {
        errorMessage = 'Authentication failed. Please check your credentials.';
      } else if (error.response.status === 403) {
        errorMessage = 'Access denied. You may not have permission to view certifications.';
      } else if (error.response.data && error.response.data.message) {
        errorMessage = error.response.data.message;
      }
    } else if (error.request) {
      // Request was made but no response received
      errorMessage = 'No response received from server. Please check your network connection.';
    } else if (error.message) {
      // Something else happened
      errorMessage = error.message;
    }


    yield put(fetchCertificationsFailure(errorMessage));
  }
}

function* fetchUsers() {
  try {
    const response = yield call(axiosConfig.get, '/user/getallusers');

    if (response.status === 200) {
      const responseData = get(response, ['data', 'data', 'data'], []);
      yield put(fetchUsersSuccess({ data: responseData }));
    } else {
      yield put(fetchUsersFailure('Failed to fetch users'));
    }
  } catch (error) {
    yield put(fetchUsersFailure(error.message || 'Failed to fetch users'));
  }
}

function* getPresignedUrl({ payload }) {
  try {
    const apiPayload = payload?.data || payload;
    const url = '/userCertificates/getPreSignedUrlToUploadCert';


    const response = yield call(axiosConfig.post, url, apiPayload);

    if (response.status === 200) {
      yield put(getPresignedUrlSuccess({ data: response.data }));
    } else {
      yield put(getPresignedUrlFailure('Failed to get presigned URL'));
    }
  } catch (error) {


    let errorMessage = 'Failed to get presigned URL';

    if (error.response) {
      // Server responded with error status


      if (error.response.status === 410) {
        errorMessage = 'API endpoint not found or resource is gone. Please check the API configuration.';
      } else if (error.response.data && error.response.data.message) {
        errorMessage = error.response.data.message;
      } else if (error.response.data && typeof error.response.data === 'string') {
        errorMessage = error.response.data;
      }
    } else if (error.request) {
      // Request was made but no response received
      errorMessage = 'No response received from server. Please check your network connection.';
    } else if (error.message) {
      // Something else happened
      errorMessage = error.message;
    }


    yield put(getPresignedUrlFailure(errorMessage));
  }
}

// Export function to get certificate presigned URL (similar to AddMaterialCategory)
export const getCertificatePresignedUrl = async (data) => {
  try {
    const url = '/userCertificates/getPreSignedUrlToUploadCert';


    const res = await axiosConfig.post(url, data);

    if (res.status === 200) {
      const responseData = get(res, ['data', 'data'], {});

      return responseData;
    } else {
      throw new Error('Failed to get certificate presigned URL');
    }
  } catch (error) {

    let errorMessage = 'Failed to get certificate presigned URL';

    if (error.response) {
      if (error.response.status === 410) {
        errorMessage = 'API endpoint not found or resource is gone. Please check the API configuration.';
      } else if (error.response.data && error.response.data.message) {
        errorMessage = error.response.data.message;
      } else if (error.response.data && typeof error.response.data === 'string') {
        errorMessage = error.response.data;
      }
    } else if (error.request) {
      errorMessage = 'No response received from server. Please check your network connection.';
    } else if (error.message) {
      errorMessage = error.message;
    }

    throw new Error(errorMessage);
  }
};


// Function to upload files to S3
export const getPreSignedUrlToUploadCert = async (fileList, payload, version = 1) => {
  try {
    const uploadedFiles = [];

    if (fileList && fileList.length > 0) {
      // Create file objects for all files
      const fileObjs = fileList.map(file => ({
        fileName: file.name.replace(/\(\d+\)/g, "").trim(), // Remove (1), (2), etc.
        contentType: file.type,
        projectId: payload.projectId,
        userId: payload.userId,
        fileSize: file.size, // Use actual file size
      }));


      // Get signed URLs for all files at once
      const response = await axiosConfig.post(`userCertificates/getPreSignedUrlToUploadCert`, fileObjs);

      if (response.status === 200) {
        const presignedData = response.data.data;

        // Process each presigned URL
        for (const data of presignedData) {
          const file = fileList.find(f => f.name.replace(/\(\d+\)/g, "").trim() === data.fileName);

          if (file) {


            // Validate presigned URL data
            if (!data.presignedUrl) {
              customNotification.error('Upload Failed', 'Failed to get upload URL from server.');
              continue; // Skip this file and continue with next
            }

            try {
              // Log the presigned URL (remove sensitive parts for security)
              const urlParts = data.presignedUrl.split('?');


              // Small delay to ensure URL is ready (optional)
              await new Promise(resolve => setTimeout(resolve, 100));

              // Upload file to S3
              const uploadResponse = await axios.put(data.presignedUrl, file, {
                headers: {
                  "Content-Type": file.type || "application/octet-stream",
                  // "Content-Disposition": `attachment; filename="${file.name}"`
                },
                // Add timeout to prevent hanging requests
                //timeout: 100000,
              });

              if (uploadResponse.status === 200) {
                uploadedFiles.push({ s3Key: data.s3Key, fileName: data.fileName });

              } else {
                customNotification.error('File Upload Failed', `Failed to upload ${file.name}. Please try again.`);
              }
            } catch (error) {
              // Add more detailed error logging
              if (error.response) {
              }
              // Show user-friendly error
              customNotification.error('File Upload Failed', `Failed to upload ${file.name}. Please try again.`);
            }
          } else {
            customNotification.error('File Upload Failed', `File ${data.fileName} not found.`);
          }
        }
      } else {
        customNotification.error('Upload Failed', 'Failed to get upload URLs from server.');
      }


    }
    return uploadedFiles;
  } catch (error) {
    return []; // Return empty array on error
  }
};


function* uploadCertificate({ payload }) {
  try {
    const { presignedUrl, file } = payload?.data || payload;

    // Upload to S3 using presigned URL
    const uploadResponse = yield call(fetch, presignedUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    });

    if (uploadResponse.ok) {
      yield put(uploadCertificateSuccess({ data: { success: true } }));
    } else {
      yield put(uploadCertificateFailure('Failed to upload certificate'));
    }
  } catch (error) {


    let errorMessage = 'Failed to upload certificate';

    if (error.message) {
      errorMessage = error.message;
    }


    yield put(uploadCertificateFailure(errorMessage));
  }
}

function* createUpdateTeamMember({ payload }) {


  try {
    const apiPayload = payload?.data || payload;


    if (apiPayload.team_member_role === 'Welder') {
      const qrPayload = {
        text: apiPayload.welder_qr_code,
        title: apiPayload.user_id,
        qr_code_type: 'Welder ID',
        bottom_text: apiPayload.Welder_Id,
        qr_size: 800,
        error_correction: 'H'
      };
     // const apiUrl = "http://localhost:8001/generate"  
     const apiUrl = "https://uawu2oh2i6.execute-api.us-west-2.amazonaws.com/dev/generate" 
      const response = yield fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': 'data360-GWjjPcCPYmyY0pzu'  // Add API key header
        },
        body: JSON.stringify(qrPayload)
      });

      const qrData = yield response.json();

      if (response.ok && qrData.success) {
        const qrImage = qrData.image;

        apiPayload.team_member_permissions = {
          ...apiPayload.team_member_permissions,
          qr_code: qrImage,
          welder_id: apiPayload.Welder_Id
        };
      }
    }
    // Ensure project_id is included in the payload
    if (!apiPayload.project_id && apiPayload.projectId) {
      apiPayload.project_id = apiPayload.projectId;
    }

    const url = '/teamMembers/createUpdateTeamMember';


    const response = yield call(axiosConfig.post, url, apiPayload);


    if (response.status === 200) {
      // Determine if this is an add or update operation
      const isUpdate = apiPayload.team_member_id || apiPayload.teamMemberId;
      const message = isUpdate ? 'Team member updated successfully.' : 'Team member added successfully.';

      customNotification.success('Success', message);
      yield put(createUpdateTeamMemberSuccess({ data: response.data }));

      // Refresh team members list after successful create/update
      // Pass the project_id if available
      const refreshPayload = apiPayload.project_id || apiPayload.projectId ?
        { projectId: apiPayload.project_id || apiPayload.projectId } : {};
      yield call(fetchTeamMembers, { payload: refreshPayload });
    } else {

      yield put(createUpdateTeamMemberFailure('Failed to create/update team member'));
    }
  } catch (error) {


    let errorMessage = 'Failed to create/update team member';

    if (error.response) {
      // Server responded with error status


      if (error.response.data && typeof error.response.data === 'object') {

      }

      if (error.response.status === 410) {
        // Show the specific error message from the data field
        if (error.response.data && error.response.data.data) {
          errorMessage = error.response.data.data;
        } else if (error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data && typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else {
          errorMessage = 'Unable to save team member. Please check your input and try again.';
        }
      } else if (error.response.status === 400) {
        // Bad request - show specific error message
        if (error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data && error.response.data.data) {
          errorMessage = error.response.data.data;
        } else if (error.response.data && typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else {
          errorMessage = 'Invalid data provided. Please check your input and try again.';
        }

        // Check for duplicate user error specifically
        if (errorMessage.toLowerCase().includes('duplicate') ||
          errorMessage.toLowerCase().includes('already exists') ||
          errorMessage.toLowerCase().includes('same user')) {
          errorMessage = 'The same user cannot be added to team members twice for the same project.';
        }
      } else if (error.response.status === 500) {
        // Server error
        errorMessage = 'Server error occurred. Please try again later.';
      } else if (error.response.status === 401) {
        errorMessage = 'Authentication failed. Please log in again.';
      } else if (error.response.status === 403) {
        errorMessage = 'Access denied. You may not have permission to perform this action.';
      } else if (error.response.data && error.response.data.message) {
        errorMessage = error.response.data.message;
      } else if (error.response.data && error.response.data.data) {
        errorMessage = error.response.data.data;
      } else if (error.response.data && typeof error.response.data === 'string') {
        errorMessage = error.response.data;
      }

      // Check for duplicate user error in any response
      if (errorMessage.toLowerCase().includes('duplicate') ||
        errorMessage.toLowerCase().includes('already exists') ||
        errorMessage.toLowerCase().includes('same user')) {
        errorMessage = 'The same user cannot be added to team members twice for the same project.';
      }
    } else if (error.request) {
      // Request was made but no response received
      errorMessage = 'No response received from server. Please check your network connection.';
    } else if (error.message) {
      // Something else happened
      errorMessage = error.message;
    }


    // Show error notification
    customNotification.error('', errorMessage);

    yield put(createUpdateTeamMemberFailure(errorMessage));

    // Don't refresh team members list on error - this prevents the "Error loading team members" message
    // from appearing when create/update fails
  }
}

function* deleteTeamMemberCert({ payload }) {
  try {
    const { certId, projectId } = payload;

    // Convert single certId to array format for the API
    const certIds = Array.isArray(certId) ? certId : [certId];

    // Make API call to delete certificate(s) using POST with certIds array
    const response = yield call(axiosConfig.post, `/teamMembers/deleteTeamMemberCert`, {
      certIds: certIds
    });

    if (response.status === 200) {
      const deletedCount = certIds.length;
      customNotification.success(
        'Certificate Deleted',
        `Team member certificate${deletedCount > 1 ? 's' : ''} has been deleted successfully.`
      );

      yield put(deleteTeamMemberCertSuccess({
        certIds: certIds,
        message: `Certificate${deletedCount > 1 ? 's' : ''} deleted successfully`
      }));

      // Refresh team members list after successful deletion
      // Use projectId from payload or fallback to state
      let refreshProjectId = projectId;

      if (!refreshProjectId) {
        const state = yield select();
        refreshProjectId = state.teamMembers?.currentProjectId || state.auth?.currentProject?.project?.projectId;
      }

      if (refreshProjectId) {
        yield call(fetchTeamMembers, { payload: { projectId: refreshProjectId } });
      }
    } else {
      throw new Error('Failed to delete certificate');
    }
  } catch (error) {
    console.error('Error deleting team member certificate:', error);

    let errorMessage = 'An unexpected error occurred while deleting the certificate.';

    if (error.response) {
      if (error.response.status === 404) {
        errorMessage = 'Certificate not found. It may have already been deleted.';
      } else if (error.response.status === 403) {
        errorMessage = 'You do not have permission to delete this certificate.';
      } else if (error.response.status === 400) {
        errorMessage = 'Invalid request. Please try again.';
      } else if (error.response.data && error.response.data.message) {
        errorMessage = error.response.data.message;
      }
    }

    customNotification.error('Error deleting certificate', errorMessage);
    yield put(deleteTeamMemberCertFailure(errorMessage));
  }
}


function* createUpdateProjectLevelRoles({ payload }) {
  console.log("Payload:", payload);
  try {
    const url = '/projectLevelRole/bulkCreateUpdateProjectLevelRoles';
    const response = yield call(axiosConfig.post, url, payload);
    if (response.status === 200) {
      yield put(createUpdateProjectLevelRolesSuccess({ data: response.data }));
    } else {
      yield put(createUpdateProjectLevelRolesFailure('Failed to create/update project level roles'));
    }
  } catch (error) {
    console.error('Error creating/updating project level roles:', error);
    let errorMessage = 'Failed to create/update project level roles';
    if (error.response && error.response.data) {
      if (error.response.data.message) {
        errorMessage = error.response.data.message;
      } else if (error.response.data.error) {
        errorMessage = error.response.data.error;
      }
    }
    yield put(createUpdateProjectLevelRolesFailure(errorMessage));
  }
}


function* fetchProjectRoles({ payload }) {
  try {
    const url = `/projectLevelRole/getProjectLevelRoles?project_id=${payload.projectId}`;
    const response = yield call(axiosConfig.get, url);
    if (response.status === 200) {
      const responseData = get(response, ['data', 'data'], []);
      yield put(fetchProjectRolesSuccess({ data: responseData }));
    } else {
      yield put(fetchProjectRolesFailure('Failed to fetch project roles'));
    }
  } catch (error) {
    yield put(fetchProjectRolesFailure(error.message || 'Failed to fetch project roles'));
  }
}

function* deleteProjectLevelRole({ payload }) {
  try {
    const { roleId, projectId } = payload || {};
    const url = `/projectLevelRole/deleteProjectLevelRole/${roleId}`;
    const response = yield call(axiosConfig.delete, url);
    if (response.status === 200) {
      customNotification.success('Success', 'Role deleted successfully.');
      yield put(deleteProjectLevelRoleSuccess({ data: response.data }));

      // Refresh project roles list after successful deletion
      if (projectId) {
        yield call(fetchProjectRoles, { payload: { projectId } });
      }
    } else {
      yield put(deleteProjectLevelRoleFailure('Failed to delete project level role'));
    }
  } catch (error) {
    console.error('Error deleting project level role:', error);
    let errorMessage = 'Failed to delete project level role';
    if (error.response && error.response.data) {
      if (error.response.data.message) {
        errorMessage = error.response.data.message;
      } else if (error.response.data.error) {
        errorMessage = error.response.data.error;
      }
    }
    customNotification.error('Error', errorMessage);
    yield put(deleteProjectLevelRoleFailure(errorMessage));
  }
}

export default function* teamMembersSaga() {
  yield all([
    takeLatest(FETCH_TEAM_MEMBERS, fetchTeamMembers),
    takeLatest(DELETE_TEAM_MEMBER, deleteTeamMember),
    takeLatest(ADD_TEAM_MEMBER, addTeamMember),
    takeLatest(UPDATE_TEAM_MEMBER, updateTeamMember),
    takeLatest(FETCH_PROJECT_ROLES, fetchProjectRoles),
    takeLatest(FETCH_CERTIFICATIONS, fetchCertifications),
    takeLatest(FETCH_USERS, fetchUsers),
    takeLatest(GET_PRESIGNED_URL, getPresignedUrl),
    takeLatest(UPLOAD_CERTIFICATE, uploadCertificate),
    takeLatest(CREATE_UPDATE_TEAM_MEMBER, createUpdateTeamMember),
    takeLatest(DELETE_TEAM_MEMBER_CERT, deleteTeamMemberCert),
    takeLatest(CREATE_UPDATE_PROJECT_LEVEL_ROLES, createUpdateProjectLevelRoles),
    takeLatest(FETCH_PROJECT_ROLES, fetchProjectRoles),
    takeLatest(DELETE_PROJECT_LEVEL_ROLE, deleteProjectLevelRole),
  ]);
} 